import { useCallback, useEffect, useState } from "react";

import { ReactComponent as EyeIcon } from "assets/svgs/Eye.svg";
import CloseButton from "components/layout/CloseButton";
import FunctionalButton from "components/layout/FunctionalButton";
import AnimatedModal from "components/layout/motion/AnimatedModal";
import { useToast } from "providers/ToastProvider";
import { fetchWifiSavedCredentials, postWifiConnect, type WifiNetwork } from "lib/wifiApi";
import {
    getStoredWifiPassword,
    rememberWifiPassword,
} from "lib/wifiPasswordStorage";
import { toUserFacingApiError } from "lib/utils/userFacingApiError";

/** Eye with slash — paired with `EyeIcon` for password visibility toggle (Heroicons outline). */
const EyeSlashIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg
        className={className}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
    >
        <path
            stroke="currentColor"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
        />
    </svg>
);

type Props = {
    visible: boolean;
    onClose: () => void;
    network: WifiNetwork | null;
    baseUrl: string | null;
    onConnected?: () => void;
};

const WifiConnectDialog: React.FC<Props> = ({
    visible,
    onClose,
    network,
    baseUrl,
    onConnected,
}) => {
    const { addToast } = useToast();
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [ipv4Method, setIpv4Method] = useState<"auto" | "manual">("auto");
    const [ipv4Address, setIpv4Address] = useState("");
    const [ipv4Gateway, setIpv4Gateway] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!visible || !network) return;

        setIpv4Method("auto");
        setIpv4Address("");
        setIpv4Gateway("");
        setSubmitting(false);

        const applyPrefill = (psk: string | null | undefined) => {
            const value = psk?.trim() ? psk : "";
            setPassword(value);
            // Show plaintext when a saved key is loaded so the Network key field
            // is visibly filled (not empty-looking behind password bullets).
            setShowPassword(Boolean(value));
        };

        const ssid = network.ssid;
        applyPrefill(getStoredWifiPassword(baseUrl, ssid));

        let cancelled = false;
        if (baseUrl) {
            void fetchWifiSavedCredentials(baseUrl, ssid)
                .then((saved) => {
                    if (cancelled) return;
                    if (saved.password) {
                        applyPrefill(saved.password);
                    }
                })
                .catch(() => {
                    /* no NM profile or older wifi-scan without this route */
                });
        }

        return () => {
            cancelled = true;
        };
    }, [visible, network?.ssid, baseUrl]);

    const handleSubmit = useCallback(async () => {
        if (!network || !baseUrl) return;
        if (ipv4Method === "manual") {
            const addr = ipv4Address.trim();
            if (!addr) {
                addToast({ type: "error", message: "Enter an IPv4 address with prefix (e.g. 192.168.1.50/24)." });
                return;
            }
            if (!addr.includes("/")) {
                addToast({
                    type: "error",
                    message: "IPv4 address should include a prefix length (e.g. /24).",
                });
                return;
            }
        }

        setSubmitting(true);
        try {
            const res = await postWifiConnect(baseUrl, {
                ssid: network.ssid,
                password,
                ipv4_method: ipv4Method,
                ipv4_address: ipv4Method === "manual" ? ipv4Address.trim() : null,
                ipv4_gateway: ipv4Method === "manual" ? ipv4Gateway.trim() || null : null,
            });
            rememberWifiPassword(baseUrl, network.ssid, password);
            addToast({
                type: "success",
                message: res.message || "Connected",
            });
            onConnected?.();
            onClose();
        } catch (e) {
            addToast({
                type: "error",
                message: toUserFacingApiError(e, "Couldn't connect to the Wi-Fi network. Try again."),
            });
        } finally {
            setSubmitting(false);
        }
    }, [
        addToast,
        baseUrl,
        ipv4Address,
        ipv4Gateway,
        ipv4Method,
        network,
        onClose,
        onConnected,
        password,
    ]);

    return (
        <AnimatedModal
            visible={Boolean(visible && network)}
            onClose={onClose}
            className="w-full max-w-md rounded-r10 border border-black80 bg-black90 p-4 shadow-lg"
            zIndexClassName="z-[10050]"
        >
            {network ? (
                <>
                    <div className="mb-3 flex items-center justify-between gap-2">
                        <h3 className="text-h4 font-semibold text-white">Connect to Wi-Fi</h3>
                        <CloseButton
                            className="text-black60 hover:text-black40"
                            onClick={onClose}
                        />
                    </div>
                    <p className="mb-4 break-words text-h5 text-black40">
                        Network: <span className="text-white">{network.ssid}</span>
                        {network.security ? (
                            <span className="text-black60"> · {network.security}</span>
                        ) : null}
                    </p>

                    <div className="space-y-3">
                        <div>
                            <label className="mb-1 block text-h5 text-black40" htmlFor="wifi-password">
                                Password
                            </label>
                            <div className="relative">
                                <input
                                    id="wifi-password"
                                    type={showPassword ? "text" : "password"}
                                    autoComplete="off"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder={
                                        network.security && network.security !== "--"
                                            ? "Network key"
                                            : "Leave empty if open"
                                    }
                                    className="w-full rounded-r5 bg-black70 py-2 pl-3 pr-11 text-h5 text-white outline-none ring-1 ring-black80 focus:ring-primary2"
                                />
                                <button
                                    type="button"
                                    className="absolute right-1 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-r3 text-black40 hover:bg-black80 hover:text-white"
                                    onClick={() => setShowPassword((v) => !v)}
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                    aria-pressed={showPassword}
                                >
                                    {showPassword ? (
                                        <EyeSlashIcon className="h-5 w-5 shrink-0" />
                                    ) : (
                                        <EyeIcon className="h-5 w-5 shrink-0 fill-current" />
                                    )}
                                </button>
                            </div>
                        </div>

                        <div>
                            <p className="mb-2 text-h5 text-black40">IPv4</p>
                            <div className="flex flex-col gap-2">
                                <label className="flex cursor-pointer items-center gap-2 text-h5 text-white">
                                    <input
                                        type="radio"
                                        name="ipv4"
                                        checked={ipv4Method === "auto"}
                                        onChange={() => setIpv4Method("auto")}
                                        className="accent-primary2"
                                    />
                                    Automatic (DHCP)
                                </label>
                                <label className="flex cursor-pointer items-center gap-2 text-h5 text-white">
                                    <input
                                        type="radio"
                                        name="ipv4"
                                        checked={ipv4Method === "manual"}
                                        onChange={() => setIpv4Method("manual")}
                                        className="accent-primary2"
                                    />
                                    Manual (static)
                                </label>
                            </div>
                        </div>

                        {ipv4Method === "manual" ? (
                            <div className="space-y-3 border-t border-black80 pt-3">
                                <div>
                                    <label className="mb-1 block text-h5 text-black40" htmlFor="wifi-ipv4-addr">
                                        Address (CIDR)
                                    </label>
                                    <input
                                        id="wifi-ipv4-addr"
                                        type="text"
                                        value={ipv4Address}
                                        onChange={(e) => setIpv4Address(e.target.value)}
                                        placeholder="192.168.1.50/24"
                                        className="w-full rounded-r5 bg-black70 px-3 py-2 text-h5 text-white outline-none ring-1 ring-black80 focus:ring-primary2"
                                    />
                                </div>
                                <div>
                                    <label className="mb-1 block text-h5 text-black40" htmlFor="wifi-ipv4-gw">
                                        Gateway (optional)
                                    </label>
                                    <input
                                        id="wifi-ipv4-gw"
                                        type="text"
                                        value={ipv4Gateway}
                                        onChange={(e) => setIpv4Gateway(e.target.value)}
                                        placeholder="192.168.1.1"
                                        className="w-full rounded-r5 bg-black70 px-3 py-2 text-h5 text-white outline-none ring-1 ring-black80 focus:ring-primary2"
                                    />
                                </div>
                            </div>
                        ) : null}
                    </div>

                    <div className="mt-6 flex flex-wrap justify-end gap-2 border-t border-black80 pt-3">
                        <FunctionalButton
                            className="rounded-r5 border border-black80 bg-black70 px-4 py-2 hover:bg-black80"
                            title="Cancel"
                            titleClassName="!normal-case text-h5"
                            disabled={submitting}
                            onClick={onClose}
                        />
                        <FunctionalButton
                            className="rounded-r5 border border-primary2 bg-primary2 px-4 py-2 hover:bg-primary1"
                            title={submitting ? "Connecting…" : "Connect"}
                            titleClassName="!normal-case text-h5"
                            disabled={submitting || !baseUrl}
                            onClick={() => void handleSubmit()}
                        />
                    </div>
                </>
            ) : null}
        </AnimatedModal>
    );
};

export default WifiConnectDialog;
